import React from "react";
import { Link } from "react-router-dom";
import { useSEO } from "@/lib/useSEO";
import { Button } from "@/components/ui/button";
import { Check, Sparkles, ShieldCheck, TrendingUp } from "lucide-react";

const wrap = (title, description, content) => {
    return function Page() {
        useSEO({ title: `${title} | Venuely`, description });
        return <div className="container-app py-16 md:py-24 max-w-3xl">{content}</div>;
    };
};

export const About = wrap("About", "About Venuely — a global private venue hire marketplace.", (
    <>
        <span className="text-xs uppercase tracking-widest text-secondary font-semibold">About</span>
        <h1 className="font-display text-4xl md:text-5xl font-semibold mt-2">Private venues, worldwide.</h1>
        <p className="mt-4 text-muted-foreground text-lg leading-relaxed">Venuely helps people plan great events by making it easy to find and enquire about private venues anywhere in the world. Birthdays, dinners, weddings, corporate events — we help you shortlist the right space in minutes, not weeks.</p>
        <p className="mt-4 text-muted-foreground text-lg leading-relaxed">We work with venues of every kind: intimate private dining rooms, rooftop terraces, cavernous warehouses, historic mansions and unusual creative spaces. Our goal is simple — clarity, quality photography, honest pricing and fast enquiries.</p>
        <p className="mt-4 text-muted-foreground text-lg leading-relaxed">Own a venue? <Link to="/list-your-venue" className="text-primary underline">List it free</Link>.</p>
    </>
));

export const HowItWorks = wrap("How it works", "How Venuely works — search venues, submit an enquiry, get responses direct from the venue.", (
    <>
        <span className="text-xs uppercase tracking-widest text-secondary font-semibold">How it works</span>
        <h1 className="font-display text-4xl md:text-5xl font-semibold mt-2">Simple, fast, free to use.</h1>
        <div className="mt-8 grid gap-6">
            {[
                { n: "01", t: "Search", d: "Filter by city, event type, capacity, budget and amenities to find your shortlist." },
                { n: "02", t: "Enquire", d: "Send a free enquiry with your event details. Message multiple venues at once if you'd like to compare." },
                { n: "03", t: "Book directly", d: "Venues respond directly by email or phone. You book and pay them — Venuely never takes a cut from your booking." },
            ].map((s) => (
                <div key={s.n} className="rounded-2xl border border-border bg-card p-6 flex gap-5">
                    <span className="font-display text-4xl font-semibold text-secondary">{s.n}</span>
                    <div><h3 className="font-display text-xl font-semibold">{s.t}</h3><p className="text-muted-foreground mt-1">{s.d}</p></div>
                </div>
            ))}
        </div>
        <div className="mt-10">
            <Button asChild className="rounded-full h-11 px-8 bg-primary text-primary-foreground"><Link to="/search">Find venues</Link></Button>
        </div>
    </>
));

export const Pricing = wrap("Pricing", "Pricing for venue owners on Venuely. Basic, Verified and Featured plans.", (
    <>
        <div className="text-center">
            <span className="text-xs uppercase tracking-widest text-secondary font-semibold">Pricing</span>
            <h1 className="font-display text-4xl md:text-5xl font-semibold mt-2">Simple plans for venue owners.</h1>
            <p className="mt-3 text-muted-foreground max-w-lg mx-auto">Enquiries are always free for guests. Venue owners can upgrade to be seen more.</p>
        </div>
        <div className="mt-10 grid md:grid-cols-3 gap-5">
            {[
                { name: "Basic", price: "Free", desc: "Get listed and start receiving enquiries.", features: ["Standard listing", "Enquiry form enabled", "Up to 5 photos"] },
                { name: "Verified", price: "£29/mo", desc: "Add trust and visibility to your listing.", features: ["Everything in Basic", "Verified badge", "Priority support", "Up to 20 photos", "Analytics"], featured: true, icon: ShieldCheck },
                { name: "Featured", price: "£99/mo", desc: "Top-of-page placement across searches.", features: ["Everything in Verified", "Featured badge", "Priority placement", "Homepage & city features"], icon: Sparkles },
            ].map((p) => (
                <div key={p.name} className={`rounded-2xl border p-6 bg-card ${p.featured ? "border-primary shadow-sm" : "border-border"}`}>
                    {p.featured && <div className="text-xs uppercase tracking-widest text-secondary font-semibold mb-2">Most popular</div>}
                    <h3 className="font-display text-2xl font-semibold">{p.name}</h3>
                    <p className="font-display text-4xl font-bold mt-2">{p.price}</p>
                    <p className="text-sm text-muted-foreground mt-2">{p.desc}</p>
                    <ul className="mt-5 space-y-2 text-sm">
                        {p.features.map((f) => <li key={f} className="flex gap-2"><Check className="h-4 w-4 text-primary mt-0.5" />{f}</li>)}
                    </ul>
                    <Button asChild className={`w-full mt-6 rounded-full ${p.featured ? "bg-primary text-primary-foreground" : ""}`} variant={p.featured ? "default" : "outline"}>
                        <Link to="/list-your-venue">Get started</Link>
                    </Button>
                </div>
            ))}
        </div>
    </>
));

export const Contact = wrap("Contact", "Get in touch with Venuely.", (
    <>
        <h1 className="font-display text-4xl md:text-5xl font-semibold">Contact</h1>
        <p className="mt-4 text-muted-foreground text-lg">For anything — support, partnerships, press — reach us at <a href="mailto:hello@venuely.com" className="text-primary underline">hello@venuely.com</a>.</p>
        <p className="mt-4 text-muted-foreground">Venue owners: use the <Link to="/list-your-venue" className="text-primary underline">List your venue</Link> flow to get started immediately.</p>
    </>
));

export const Terms = wrap("Terms of Service", "Venuely's terms of service.", (
    <>
        <h1 className="font-display text-4xl md:text-5xl font-semibold">Terms</h1>
        <p className="mt-4 text-muted-foreground">By using Venuely you agree to these terms of service. Venuely is a marketplace connecting people planning private events with venues. All bookings and payments happen directly between the guest and the venue.</p>
    </>
));

export const Privacy = wrap("Privacy Policy", "Venuely's privacy policy.", (
    <>
        <h1 className="font-display text-4xl md:text-5xl font-semibold">Privacy</h1>
        <p className="mt-4 text-muted-foreground">We store enquiry information solely to connect you with venues and improve our service. We never sell your data. See our full policy on request.</p>
    </>
));
